import { motion } from "framer-motion";
import {
  Calendar,
  CreditCard,
  DollarSign,
  Download,
  TrendingUp,
} from "lucide-react";
import { useState } from "react";
import { fadeIn, fadeInUp, stagger } from "../../lib/motion";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { PageHeader } from "../ui/page-header";

const ease = [0.16, 1, 0.3, 1] as const;

export function BillingView() {
  const [currentPlan] = useState("Starter");
  const [usageData] = useState({
    buildMinutes: { used: 120, limit: 500 },
    bandwidth: { used: 2.3, limit: 10 },
    storage: { used: 0.8, limit: 5 },
  });

  const [billingHistory] = useState([
    { id: "1", date: "2024-01-01", amount: 0, status: "paid", plan: "Starter" },
    { id: "2", date: "2023-12-01", amount: 0, status: "paid", plan: "Starter" },
  ]);

  const pct = (used: number, limit: number) =>
    Math.round((used / limit) * 100);

  const barColor = (p: number) => {
    if (p >= 90) return "bg-destructive";
    if (p >= 70) return "bg-warning";
    return "bg-success";
  };

  const usageCards = [
    {
      label: "Build Minutes",
      used: usageData.buildMinutes.used,
      limit: usageData.buildMinutes.limit,
      unit: "",
    },
    {
      label: "Bandwidth",
      used: usageData.bandwidth.used,
      limit: usageData.bandwidth.limit,
      unit: " GB",
    },
    {
      label: "Storage",
      used: usageData.storage.used,
      limit: usageData.storage.limit,
      unit: " GB",
    },
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <motion.div
        className="flex items-center justify-between"
        variants={fadeIn}
        initial="hidden"
        animate="visible"
      >
        <PageHeader
          eyebrow="Account"
          title="Billing & Usage"
          description="Manage your subscription and view usage metrics"
          className="w-full"
        />
      </motion.div>

      {/* Current Plan */}
      <motion.div variants={fadeInUp} initial="hidden" animate="visible">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <CreditCard className="mr-2 h-5 w-5" />
              Current Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-h2">{currentPlan}</h3>
                <p className="text-muted-foreground">
                  Free tier with generous limits
                </p>
                <p className="mt-2 text-sm font-medium">$0.00 / month</p>
              </div>
              <div className="text-right">
                <Badge variant="secondary" className="mb-2">
                  Current Plan
                </Badge>
                <br />
                <Button variant="outline" size="sm">
                  Upgrade Plan
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Usage Overview */}
      <motion.div
        className="grid grid-cols-1 gap-6 md:grid-cols-3"
        variants={stagger}
        initial="hidden"
        animate="visible"
      >
        {usageCards.map(({ label, used, limit, unit }) => {
          const p = pct(used, limit);
          return (
            <motion.div key={label} variants={fadeInUp}>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">{label}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-h2">
                        {used}
                        {unit}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        / {limit}
                        {unit}
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
                      <motion.div
                        className={`h-2 rounded-full ${barColor(p)}`}
                        initial={{ width: 0 }}
                        animate={{ width: `${p}%` }}
                        transition={{ duration: 0.9, ease, delay: 0.25 }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">{p}% used</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Billing History */}
      <motion.div variants={fadeInUp} initial="hidden" animate="visible">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <Calendar className="mr-2 h-5 w-5" />
                Billing History
              </div>
              <Button variant="outline" size="sm">
                <Download className="mr-2 h-4 w-4" />
                Download All
              </Button>
            </CardTitle>
            <CardDescription>
              View your past invoices and payment history
            </CardDescription>
          </CardHeader>
          <CardContent>
            <motion.div
              className="space-y-4"
              variants={stagger}
              initial="hidden"
              animate="visible"
            >
              {billingHistory.map((invoice) => (
                <motion.div
                  key={invoice.id}
                  variants={fadeInUp}
                  className="flex items-center justify-between rounded-lg bg-card p-4"
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary">
                      <DollarSign className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {new Date(invoice.date).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {invoice.plan} Plan
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="text-right">
                      <p className="font-medium">
                        ${invoice.amount.toFixed(2)}
                      </p>
                      <Badge
                        variant={
                          invoice.status === "paid" ? "default" : "secondary"
                        }
                        className="text-xs"
                      >
                        {invoice.status}
                      </Badge>
                    </div>
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Usage Trends */}
      <motion.div variants={fadeInUp} initial="hidden" animate="visible">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <TrendingUp className="mr-2 h-5 w-5" />
              Usage Trends
            </CardTitle>
            <CardDescription>
              Track your resource usage over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
              <TrendingUp className="h-8 w-8 text-muted-foreground/30" />
              <p className="text-sm font-medium text-muted-foreground">Usage trends coming soon</p>
              <p className="text-xs text-muted-foreground/60 max-w-xs">
                Charts and historical usage data will appear here once the feature is available.
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
